"use client";

import type { ProtocolMetadata } from "@/types";
import React, { useMemo, type PropsWithChildren } from "react";

import { SupHeading } from "@repo/core-ui";

import ProtocolMenu from "@/components/Protocol/Menu";
import UiDetailsHeader from "@/components/Ui/Details/Header";
import useProtocolColor from "@/hooks/data/useProtocolColor";
import UiDetailsAtomLogo from "../Ui/Details/Atom/Logo";
import UiDetailsAtomLinks from "../Ui/Details/Atom/Links";
import UiDetailsBreadcrumbs, { type DetailsBreadCrumb } from "../Ui/Details/Atom/Breadcrumbs";
import UiDetailsAtomStatus from "../Ui/Details/Atom/Status";
import ProtocolStats from "./Stats";

import dictionaryConfig from "@/../config/dictionary.config";
import { twJoin } from "tailwind-merge";

interface ProtocolHeaderProps extends PropsWithChildren {
  singleProtocol: ProtocolMetadata;
  currentTab: string;
  slug: string;
}

export default function ProtocolHeader({ singleProtocol, currentTab, slug }: ProtocolHeaderProps) {
  const colors = useProtocolColor(singleProtocol, "#C3BBEC");
  const logo = singleProtocol?.graphics.icon || singleProtocol?.graphics.avatar || dictionaryConfig.protocol.noProtocolImage;
  const breadCrumbs: DetailsBreadCrumb[] = useMemo(
    () => [
      {
        href: "/protocols/",
        name: "Protocols",
      },
      { name: singleProtocol?.name ?? "" },
    ],
    [singleProtocol?.name]
  );

  return (
    singleProtocol && (
      <>
        <UiDetailsHeader
          banner={singleProtocol?.is_authenticated ? singleProtocol?.graphics.header : undefined}
          links={<UiDetailsAtomLinks links={singleProtocol.links} shareLink={`protocol/${singleProtocol.vanity_url ? singleProtocol.vanity_url : singleProtocol.id}`} />}
        >
          {singleProtocol.is_authenticated && (
            <UiDetailsAtomLogo logo={logo} title={singleProtocol?.name} className="relative -translate-y-2 md:absolute md:left-6 md:top-0 md:-translate-y-1/2" />
          )}
        </UiDetailsHeader>

        <div className="grid grid-cols-2">
          <div className="col-span-2 flex flex-1 flex-col items-center px-4 md:order-2 md:flex-col-reverse md:items-start md:px-6 xl:col-span-1">
            <UiDetailsBreadcrumbs className={twJoin("no-scrollbar max-w-full overflow-y-auto", !singleProtocol.is_authenticated ? "mb-2 mt-4" : "")} breadCrumbs={breadCrumbs} />

            <SupHeading
              variant="h2"
              family="sans"
              weight="black"
              className="max-w-full text-center text-3xl text-neutral-925 md:mb-4 md:overflow-hidden md:text-ellipsis md:whitespace-nowrap md:text-left md:text-5xl xl:mb-9 xl:text-7xl"
            >
              {singleProtocol?.name}
            </SupHeading>

            <UiDetailsAtomStatus auth={singleProtocol.is_authenticated} logo={logo} />
          </div>

          <div className="col-span-2 my-6 flex justify-center md:order-1 md:mt-2 md:justify-end xl:mb-2 xl:mt-6">
            <ProtocolMenu className="w-fit" slug={slug as string} currentTab={currentTab} colors={colors} />
          </div>

          <div className="col-span-2 flex px-4 md:order-3 md:mt-8 md:px-6 xl:col-span-1 xl:mt-0 xl:items-end">
            <ProtocolStats singleProtocol={singleProtocol} />
          </div>
        </div>
      </>
    )
  );
}
